import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { InterfacePafButton } from './paf-button.custom.d';

/**
 * @slot unnamed - Slot to render svg icon. You must pass an svg-element.
 */

@Component({
  shadow: true,
  styleUrl: 'paf-button.scss',
  tag: 'paf-button',
})

export class PafButton implements ComponentInterface {

  /**
   * The text for the button.
   */
  @Prop() public text?: string;

  /**
   * The variant for the button. Available values are primary and secondary
   */
  @Prop() public variant?: InterfacePafButton['variant'] = 'primary';

  /**
   * If set to true, the button will be disabled.
   */
  @Prop() public disabled? = false;

  /**
   * If set to true, the button will be rendered as a link (semantically)
   */
  @Prop() public isLink? = false;

  /**
   * If the button is rendered as link (see isLink), you should provide
   * a href attribute for the link
   */
  @Prop() public href?: string;

  /**
   * If the button is rendered as link (see isLink), you should provide
   * a target attribute for the link
   */
  @Prop() public target?: InterfacePafButton['linkTarget'];

  /** The type attribute to use for the button */
  @Prop() public type?: InterfacePafButton['type'] = 'button';

  /**
   * If defined, this will be used as aria-label attribute on the host element.
   */
  @Prop({
    reflect: true,
  }) public aLabelValue?: string;

  /**
   * Set this value to true if you pass an svg icon to the component.
   * (see slot-documentation)
   * Note: due to various different implementations of icon-buttons, the
   * button-variant with icon-only will spread across 100% width and height.
   * This means your implementation is responsible to set it's width and
   * height appropriately.
   */
  @Prop() public icon? = false;

  /**
   * If you use an icon without a label, you must provide an iconDescription
   */
  @Prop() public iconDescription?: string;

  /**
   * If the button is used as a link with target="_blank", then you should
   * provide a text that we append to the link for screenreaders.
   * Example: "Link target opens in a new window"
   */
  @Prop() public hiddenTextOpensInNewWindow?: string;

  /**
   * If set to true, the icon will be rotated by 90degrees
   */
  @Prop() public rotateIcon?;

  /**
   * If type is expandable, you may define if it is expanded initially
   */
  @Prop() public expanded?;

  /**
   * If set to true, preventDefault will be enforced in the click handler,
   * no matter if the component is a button or a link
   *
   * example: button on search form might be a link. But we don't want to
   * follow the href immediately, but first do some work (append params)
   */
  @Prop() public forcePreventDefault = false;

  /**
   * Event which will be emitted if the button is clicked
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-button_clicked',
  })
  public clicked: EventEmitter<void>;

  /**
   * Event which will be emitted if a keydown event has
   * been noted on the expandable button
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-button_expandable-keydown',
  })
  public expandableKeyDown: EventEmitter<void>;

  /**
   * Event which will be emitted if the expandable button state changes.
   * The payload will return true if the button is expanded, false otherwise.
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-button_expandable-changed',
  })
  public expandableChanged: EventEmitter<boolean>;

  @Watch('expanded')
  public expandedWatcher(newValue: boolean): void {
    this._expanded = newValue;
  }

  @State() private _expanded = false;

  private _buttonClick = (evt: any): void => {

    // don't emit events for link variant
    if (this.isLink) {
      if (this.forcePreventDefault) {
        evt.preventDefault();
      } else {
        this.clicked.emit();

        return;
      }
    }

    evt.preventDefault();

    // toggle state for expandable variant
    if (this.variant === 'expandable') {
      this._expanded = !this._expanded;
    }

    // emit expandable event
    if (this.variant === 'expandable') {
      this.expandableChanged.emit(this._expanded);

      return;
    }

    this.clicked.emit();
  };

  private _buttonKeyDown = (evt: any): void => {
    if (this.variant === 'expandable') {
      this.expandableKeyDown.emit(evt.detail);
    }
  };

  public componentWillLoad(): Promise<void> | void {
    this._expanded = this.expanded;
  }

  public render(): HTMLDivElement {
    const hasNoText = !this.text || this.text.length < 1;

    // security exit, if neither label nor icon is provided via props
    if (hasNoText && !this.icon) {
      return <p>Config error: either label or icon must be provided</p>;
    }

    // security exit for icon only button with no icon description
    if (hasNoText && this.icon && !this.iconDescription) {
      return <p>Config error: you must provide an icon description</p>;
    }

    const TAGNAME = this.isLink
      ? 'a'
      : 'button';

    const elementAttributes = {};

    if (this.isLink) {
      elementAttributes['href'] = this.href;
      elementAttributes['target'] = this.target;
    }

    if (this.icon && hasNoText && this.iconDescription) {
      elementAttributes['aria-label'] = this.iconDescription;
    }

    if (this.variant === 'expandable') {
      elementAttributes['aria-expanded'] = this._expanded?.toString() === 'true'
        ? 'true'
        : 'false';
    }

    if (this.aLabelValue && this.aLabelValue.length > 0) {
      elementAttributes['aria-label'] = this.aLabelValue;
    }

    if (this.disabled) {
      elementAttributes['tabindex'] = '-1';
    }

    let textWithOptionalHiddenText = this.text;

    if (!hasNoText && this.isLink && this.target === '_blank' && this.hiddenTextOpensInNewWindow) {
      textWithOptionalHiddenText += `<span data-testid="hidden-link-text" class="hidden-text"> ${this.hiddenTextOpensInNewWindow}</span>`;
    }

    return (
      <TAGNAME
        onClick={this._buttonClick}
        onKeyDown={this._buttonKeyDown}
        class={{
          'button': true,
          [this.variant]: true,
          'expanded': this._expanded,
          'icon-only': hasNoText,
          'rotate-icon': this.rotateIcon,
        }}
        disabled={this.disabled}
        type={
          this.isLink
            ? undefined
            : this.type
        }
        {...elementAttributes}
        data-testid='button'
      >
        {this.icon
          ? <span
            class='icon'
            aria-hidden='true'
            role='presentation'
          ><slot /></span>
          : ''
        }

        {hasNoText
          ? ''
          : <span class='text' innerHTML={textWithOptionalHiddenText} />
        }

        {this.variant === 'expandable' &&
          <svg
            class='expand-icon'
            viewBox='0 0 16 9'
          >
            <path d='M8 .25.167 8l.667.667L8 1.417l7.25 7.25.583-.667L8 .25Z' />
          </svg>
        }
      </TAGNAME>
    );
  }
}
